import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import fixswiftLogo from "@/assets/fixswift-logo.svg";
import { useCMSContent } from "@/hooks/useCMSContent";

const FALLBACK_FOOTER = {
  company_name: "FixSwift Computers LLP",
  tagline: "Certified refurbished laptops & desktops with warranty, GST billing & PAN India delivery.",
  email: "info@fixswift.in",
  phone: "+91 99990 97873",
};

const Footer = () => {
  const { data: cmsSections = [] } = useCMSContent();
  const footerContent = cmsSections.find((s) => s.section_key === "footer")?.content ?? FALLBACK_FOOTER;
  const footer = {
    company_name: footerContent.company_name || FALLBACK_FOOTER.company_name,
    tagline: footerContent.tagline || FALLBACK_FOOTER.tagline,
    phone: footerContent.phone || FALLBACK_FOOTER.phone,
    email: footerContent.email || FALLBACK_FOOTER.email,
  };

  const linkGroups = [
    {
      title: "Shop",
      links: [
        { to: "/products", label: "All Products" },
        { to: "/products?category=laptop", label: "Laptops" },
        { to: "/products?category=desktops", label: "Desktops" },
        { to: "/sell", label: "Sell Your Device" },
      ],
    },
    {
      title: "Company",
      links: [
        { to: "/about-us", label: "About Us" },
        { to: "/why-refurbished", label: "Why Refurbished?" },
        { to: "/bulk-order", label: "Bulk Orders" },
        { to: "/contact-us", label: "Contact" },
      ],
    },
    {
      title: "Support",
      links: [
        { to: "/account", label: "My Orders" },
        { to: "/repair", label: "Book Repair" },
        { to: "/repair/track", label: "Track Repair" },
        { to: "/warranty-policy", label: "Warranty" },
      ],
    },
  ];

  return (
    <footer className="bg-foreground text-background relative pb-20 md:pb-0">
      <div className="px-4 md:container md:max-w-7xl py-4 lg:py-16">
        {/* Top: Brand + CTA */}
        <div className="flex items-center justify-between gap-4 mb-2 lg:mb-10">
          <div>
            <Link to="/" className="inline-flex items-center gap-3 group">
              <div className="h-7 w-7 lg:h-11 lg:w-11 bg-background rounded-lg lg:rounded-xl flex items-center justify-center">
                <img src={fixswiftLogo} alt="Fixswift" className="h-4 w-4 lg:h-6 lg:w-6" />
              </div>
              <span className="font-heading text-base lg:text-2xl font-black tracking-widest uppercase text-background">
                FixSwift
              </span>
            </Link>
            <p className="hidden lg:block text-[15px] leading-relaxed text-background/50 mt-3 max-w-lg">
              {footer.tagline}
            </p>
          </div>
          <a
            href={`https://wa.me/${footer.phone.replace(/[^0-9]/g, "")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg lg:rounded-xl bg-primary px-4 lg:px-7 py-2 lg:py-3.5 font-bold text-xs lg:text-[15px] text-primary-foreground hover:bg-primary/90 transition-all group shrink-0"
          >
            Chat on WhatsApp
            <ArrowUpRight className="h-3.5 w-3.5 lg:h-4.5 lg:w-4.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        {/* Mobile-only tagline */}
        <p className="text-[11px] leading-relaxed text-background/45 mb-3 lg:hidden max-w-md">
          {footer.tagline}
        </p>

        <div className="h-px bg-background/8 mb-3 lg:mb-10" />

        {/* Links Grid */}
        <div className="grid grid-cols-3 lg:grid-cols-5 gap-x-2 gap-y-3 lg:gap-x-12 lg:gap-y-0">
          {linkGroups.map((group) => (
            <div key={group.title}>
              <h4 className="text-[9px] lg:text-xs font-bold uppercase tracking-[0.15em] text-background/35 mb-1.5 lg:mb-5">
                {group.title}
              </h4>
              <ul className="space-y-1 lg:space-y-3.5">
                {group.links.map(({ to, label }) => (
                  <li key={label}>
                    <Link to={to} className="text-[11px] lg:text-[15px] font-medium text-background/55 hover:text-background transition-colors">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact */}
          <div className="col-span-3 lg:col-span-2 mt-1 lg:mt-0">
            <h4 className="text-[9px] lg:text-xs font-bold uppercase tracking-[0.15em] text-background/35 mb-1.5 lg:mb-5">
              Get in Touch
            </h4>
            <div className="flex flex-wrap gap-x-4 gap-y-1 lg:flex-col lg:gap-y-4">
              <a href={`tel:${footer.phone.replace(/\s/g, "")}`} className="flex items-center gap-1.5 lg:gap-2.5 text-[11px] lg:text-[15px] font-medium text-background/55 hover:text-background transition-colors">
                <Phone className="h-3 w-3 lg:h-[18px] lg:w-[18px] text-primary shrink-0" />
                {footer.phone}
              </a>
              <a href={`mailto:${footer.email}`} className="flex items-center gap-1.5 lg:gap-2.5 text-[11px] lg:text-[15px] font-medium text-background/55 hover:text-background transition-colors">
                <Mail className="h-3 w-3 lg:h-[18px] lg:w-[18px] text-primary shrink-0" />
                {footer.email}
              </a>
              <div className="flex items-center gap-1.5 lg:gap-2.5 text-[11px] lg:text-[15px] font-medium text-background/55">
                <MapPin className="h-3 w-3 lg:h-[18px] lg:w-[18px] text-primary shrink-0" />
                <span>Gurugram, Haryana</span>
              </div>
            </div>
            <div className="mt-2 lg:mt-5 flex flex-wrap gap-1 lg:gap-2">
              {["6-Month Warranty", "GST Invoice", "PAN India Delivery"].map((badge) => (
                <span key={badge} className="text-[9px] lg:text-[11px] font-semibold text-background/40 bg-background/5 border border-background/10 rounded-full px-2 lg:px-3.5 py-0.5 lg:py-1.5">
                  {badge}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-background/8">
        <div className="px-4 md:container md:max-w-7xl flex items-center justify-between gap-1.5 py-2.5 lg:py-5">
          <p className="text-[10px] lg:text-[13px] font-medium text-background/30">
            © {new Date().getFullYear()} {footer.company_name}
          </p>
          <div className="flex items-center gap-3 lg:gap-6 text-[10px] lg:text-[13px] font-medium text-background/30">
            <Link to="/terms" className="hover:text-background/60 transition-colors">Terms</Link>
            <Link to="/privacy-policy" className="hover:text-background/60 transition-colors">Privacy</Link>
            <Link to="/refund_returns" className="hover:text-background/60 transition-colors">Returns</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
